import { useEffect, useState } from 'react'
import { useCpStore } from '@/stores/useCpStore'
import { cn } from '@/lib/utils'
import { Check, AlertCircle, Loader2, X, Clock } from 'lucide-react'

const STAGE_ORDER = ['ingest', 'cardify', 'embed', 'canonicalize', 'link'] as const
type Stage = (typeof STAGE_ORDER)[number]

const STAGE_LABELS: Record<Stage, string> = {
  ingest: 'Ingest',
  cardify: 'Extract',
  embed: 'Embed',
  canonicalize: 'Dedupe',
  link: 'Link',
}

export function CpStatusBar() {
  const { statusBar, clearStatusBar, workflowQueue } = useCpStore()
  const [visible, setVisible] = useState(false)
  const [shouldRender, setShouldRender] = useState(false)

  // Handle visibility transitions
  useEffect(() => {
    if (statusBar.type === 'idle') {
      setVisible(false)
      // Delay unmount for exit animation
      const timer = setTimeout(() => setShouldRender(false), 200)
      return () => clearTimeout(timer)
    } else if (statusBar.type === 'success') {
      setVisible(true)
      setShouldRender(true)
      // Auto-fade success after delay
      const timer = setTimeout(() => {
        setVisible(false)
        setTimeout(() => {
          clearStatusBar()
        }, 200)
      }, 3000)
      return () => clearTimeout(timer)
    } else {
      setVisible(true)
      setShouldRender(true)
    }
  }, [statusBar, clearStatusBar])

  if (!shouldRender) return null

  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 right-0 z-50',
        'bg-background/95 backdrop-blur-sm border-t border-border',
        'transition-all duration-200 ease-out',
        visible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      )}
    >
      <div className="h-8 px-4 flex items-center gap-4 text-xs">
        {statusBar.type === 'processing' && (
          <>
            {/* Stage dots */}
            <div className="flex items-center gap-1">
              {STAGE_ORDER.map((stage) => {
                const currentStage = statusBar.workflow.progress.stage
                const currentIndex = STAGE_ORDER.indexOf(currentStage as Stage)
                const stageIndex = STAGE_ORDER.indexOf(stage)
                const isComplete = stageIndex < currentIndex
                const isCurrent = stage === currentStage
                const isPending = stageIndex > currentIndex

                return (
                  <div
                    key={stage}
                    className={cn(
                      'flex items-center gap-1',
                      isPending && 'opacity-40'
                    )}
                    title={STAGE_LABELS[stage]}
                  >
                    {isCurrent ? (
                      <Loader2
                        className="w-3 h-3 animate-spin text-foreground"
                        strokeWidth={2}
                      />
                    ) : isComplete ? (
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                    ) : (
                      <div className="w-2 h-2 rounded-full bg-muted-foreground/30" />
                    )}
                  </div>
                )
              })}
            </div>

            {/* Progress text */}
            <span className="text-muted-foreground flex-1 truncate">
              {statusBar.workflow.progress.message}
            </span>

            {/* Queue badge */}
            {(statusBar.queueCount > 0 || workflowQueue.length > 0) && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-muted rounded-full text-muted-foreground">
                <Clock className="w-3 h-3" strokeWidth={1.5} />
                {statusBar.queueCount || workflowQueue.length} queued
              </span>
            )}
          </>
        )}

        {statusBar.type === 'success' && (
          <>
            {/* Success icon */}
            <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
              <Check className="w-3.5 h-3.5" strokeWidth={2} />
              <span className="font-medium">
                {statusBar.cardsCreated} cards, {statusBar.edgesCreated} links
              </span>
            </div>

            {/* Close button */}
            <button
              onClick={clearStatusBar}
              className="ml-auto p-1 text-muted-foreground hover:text-foreground rounded transition-colors"
            >
              <X className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
          </>
        )}

        {statusBar.type === 'error' && (
          <>
            {/* Error icon and message */}
            <div className="flex items-center gap-1.5 text-destructive flex-1 min-w-0">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={2} />
              <span className="truncate">{statusBar.message}</span>
            </div>

            {/* Close button */}
            <button
              onClick={clearStatusBar}
              className="p-1 text-muted-foreground hover:text-foreground rounded transition-colors"
            >
              <X className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
          </>
        )}
      </div>
    </div>
  )
}
